import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Star, Target, TrendingUp } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";
import { useTranslation } from "react-i18next";

export default function ProfileSidebar() {
  const { t } = useTranslation("profile");
  const { user, profile } = useAuth();

  // Fetch booking stats
  const { data: stats } = useQuery({
    queryKey: ["profile-sidebar-stats", user?.id],
    queryFn: async () => {
      const { count: totalBookings } = await supabase
        .from("bookings")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user!.id);

      return {
        totalBookings: totalBookings ?? 0,
      };
    },
    enabled: !!user?.id,
  });

  // Fetch membership
  const { data: membership } = useQuery({
    queryKey: ["profile-sidebar-membership", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_memberships")
        .select("*, membership_plans(name, price, interval)")
        .eq("user_id", user!.id)
        .eq("status", "active")
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch wallet balance
  const { data: wallet } = useQuery({
    queryKey: ["profile-sidebar-wallet", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("wallets")
        .select("balance")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id,
  });

  const currentLevel = profile?.level || 1;

  return (
    <div className="space-y-4">
      {/* Membership Card */}
      {membership && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              {t("membership")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="font-medium text-sm">
              {(membership as any).membership_plans?.name}
            </p>
            <p className="text-xs text-muted-foreground">
              {(membership as any).membership_plans?.price} kr /{" "}
              {(membership as any).membership_plans?.interval === "month"
                ? t("common:month", "mån")
                : t("common:year", "år")}
            </p>
            <Badge variant="secondary" className="text-xs">
              {t("common:status.active", "Aktiv")}
            </Badge>
          </CardContent>
        </Card>
      )}

      {/* Wallet */}
      {wallet && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Star className="h-4 w-4" />
              {t("payments")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{wallet.balance ?? 0} kr</p>
            <p className="text-xs text-muted-foreground">
              {t("common:upcoming", "Saldo")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Play Profile */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="h-4 w-4" />
            {t("playProfile", "Play profile")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{t("level")}</span>
            <LevelBadge level={currentLevel} size="sm" />
          </div>
          {profile?.preferred_hand && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t("preferredHand")}
              </span>
              <span className="text-sm">
                {t(profile.preferred_hand as "left" | "right" | "both")}
              </span>
            </div>
          )}
          {profile?.player_type && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t("playerType")}
              </span>
              <span className="text-sm">
                {t(
                  profile.player_type as
                    | "offensive"
                    | "defensive"
                    | "allround"
                )}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            {t("statistics")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {t("myBookings")}
            </span>
            <span className="text-sm font-medium">
              {stats?.totalBookings ?? 0}
            </span>
          </div>
          {profile?.wins != null && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t("winRate")}
              </span>
              <span className="text-sm font-medium">
                {profile.wins + (profile.losses ?? 0) > 0
                  ? Math.round(
                      (profile.wins /
                        (profile.wins + (profile.losses ?? 0))) *
                        100
                    )
                  : 0}
                %
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{t("wins")}</span>
            <span className="text-sm font-medium">{profile?.wins ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {t("losses")}
            </span>
            <span className="text-sm font-medium">
              {profile?.losses ?? 0}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
